import React, { useState, useEffect } from "react";
import uuid from "short-uuid";
import { Row, Col } from "react-bootstrap";
import { ReOrderableItem, ReOrderableList } from "react-reorderable-list";
import { useSelector, useDispatch } from "react-redux";
import { sendErrorNotification } from "../../../../universal/notifications/notifications";
import { ToastContainer } from "react-toastify";
import {
  changePipelineModal,
  requestDeleteSinglePipeline,
  requestUpdateSinglePipeline,
} from "../../../../../redux/actions/pipeline";
import {
  TransparentButton,
  SolidButton,
  FadeButton,
} from "../../../../library/button";
import { Input } from "../../../../library/input";
import { Popup } from "../../../../library/popup";
import "react-reorderable-list/dist/index.css";
import { Label } from "../../../../library/label";
import "./style.scss";

const Index = () => {
  const dispatch = useDispatch();

  const modalState = useSelector(
    (state) => state.pipeline.showModals.editPipelineModal
  );

  const pipelineId = useSelector(
    (state) => state.pipeline.activePipeline && state.pipeline.activePipeline.id
  );
  const editPipeline = useSelector((state) => state.pipeline.editPipeline);

  const deleteLoading = useSelector(
    (state) => state.pipeline.deletePipeline.loading
  );
  const updateLoading = useSelector(
    (state) => state.pipeline.updatePipeline.loading
  );

  const selectedCompany = useSelector(
    (state) =>
      state &&
      state.activeCompany &&
      state.activeCompany.activeCompany &&
      state.activeCompany.activeCompany.id
  );
  const allPipelines = useSelector((state) => state.pipeline.selections);

  const [newPipelineName, setNewPipelineName] = useState("");
  const [singleUnitName, setSingleUnitName] = useState("");
  const [pipelineFlow, setPipelineFlow] = useState([]);
  const [unitDeletions, setUnitDeletions] = useState([]);

  const handleClose = () => {
    dispatch(changePipelineModal({ editPipelineModal: false }));
  };

  const clickDeletePipeline = () => {
    if (allPipelines && allPipelines.length > 1) {
      dispatch(
        requestDeleteSinglePipeline({
          companyId: selectedCompany,
          pipelineId: pipelineId,
        })
      );
    } else {
      sendErrorNotification("Du må ha minst èn pipeline.");
    }
  };

  const clickUpdatePipeline = () => {
    if (pipelineFlow.length > 0 && newPipelineName) {
      const pipelineUnits = [];
      pipelineFlow.forEach((unit, index) => {
        pipelineUnits.push({
          name: unit.name,
          order: parseInt(index),
          id: unit.id,
        });
      });
      dispatch(
        requestUpdateSinglePipeline({
          companyId: selectedCompany,
          pipelineId: pipelineId,
          name: newPipelineName,
          units: pipelineUnits,
          deletions: unitDeletions,
        })
      );
    } else {
      sendErrorNotification(
        "Du må oppgi et navn og ha hvertfall èn fase i pipeline."
      );
    }
  };

  const clickAddPipelinePhase = () => {
    if (singleUnitName) {
      setPipelineFlow([
        ...pipelineFlow,
        { id: uuid.generate(), name: singleUnitName },
      ]);
      setSingleUnitName("");
    } else {
      sendErrorNotification("Vennligst skriv inn et fasenavn.");
    }
  };

  const clickDeletePipelinePhase = (id) => {
    if (
      editPipeline.units &&
      editPipeline.units.filter((e) => e.id === id).length > 0
    ) {
      setUnitDeletions([...unitDeletions, id]);
    }

    const newPipeline = pipelineFlow.filter((item) => item.id !== id);
    setPipelineFlow(newPipeline);
  };

  useEffect(() => {
    if (editPipeline && editPipeline.id) {
      setPipelineFlow(editPipeline.units);
      setNewPipelineName(editPipeline.name);
    }
  }, [editPipeline]);

  return (
    <Popup
      onClose={handleClose}
      show={modalState}
      title="Endre pipeline"
      buttons={[
        <TransparentButton
          title="Avbryt"
          id="closeButtonDelete"
          type="button"
          data-dismiss="modal"
          onClick={() => handleClose()}
        />,
        <SolidButton
          color="red"
          onClick={() => clickDeletePipeline()}
          type="button"
          icon={
            deleteLoading ? (
              <i className="fas fa-circle-notch fa-spin"></i>
            ) : (
              <i className="fas fa-trash-alt"></i>
            )
          }
          title={deleteLoading ? "Sletter pipeline.." : "Slett pipeline"}
        />,
        <SolidButton
          color="blue"
          title={updateLoading ? "Oppdaterer.." : "Oppdater"}
          icon={
            updateLoading ? (
              <i className="fas fa-circle-notch fa-spin"></i>
            ) : (
              <i className="fas fa-sync"></i>
            )
          }
          onClick={() => clickUpdatePipeline()}
          type="button"
        />,
      ]}
    >
      <div className="edit-pipeline-content-container">
        <Row className="info-row">
          <Col className="search-col align-self-end">
            <Label version={1} weight="bold">
              Navn
            </Label>
            <Input
              value={newPipelineName}
              onChange={(e) => setNewPipelineName(e.target.value)}
              placeholder="Min pipeline"
              type="text"
            />
          </Col>
        </Row>
        <hr />
        <Row className="info-row">
          <Col className="search-col align-self-end">
            <Label version={1} weight="bold">
              Fasenavn
            </Label>
            <Input
              value={singleUnitName}
              onChange={(e) => setSingleUnitName(e.target.value)}
              placeholder="Fase èn"
              type="text"
            />
          </Col>
          <Col className="button-col align-self-end">
            <FadeButton
              color="blue"
              icon={<i class="fas fa-plus"></i>}
              onClick={() => clickAddPipelinePhase()}
              title="Legg til"
            />
          </Col>
        </Row>
        <Row className="info-row">
          <Col className="info-col">
            {pipelineFlow.length > 0 ? (
              <div
                className="draggable-tree"
                style={{
                  display: "block",
                  gap: "20px",
                  backgroundColor: "rgb(244, 246, 249);",
                }}
              >
                <div className="inner-draggable-tree">
                  <ReOrderableList
                    list={pipelineFlow}
                    onListUpdate={(newPipeline) => setPipelineFlow(newPipeline)}
                    className="single-tree-group"
                    style={{ width: "100%" }}
                  >
                    {pipelineFlow.map((data, index) => (
                      <ReOrderableItem
                        className="single-tree-item"
                        key={`item-${index}`}
                      >
                        <div
                          style={{
                            backgroundColor: "#FFF",
                            borderRadius: "6px",
                          }}
                          className="inner-single-tree-item"
                        >
                          <div
                            style={{
                              padding: "10px",
                              marginTop: "10px",
                              marginBottom: "10px",
                            }}
                            className="single-tree-content"
                          >
                            <Row>
                              <Col className="name-col text-left">
                                <span
                                  style={{
                                    fontFamily: "Questrial, sans-serif",
                                    color: "rgb(68, 68, 68)",
                                    fontSize: "14px",
                                  }}
                                  className="phase-name"
                                >
                                  <span className="order">{index}.</span>{" "}
                                  {data.name}
                                </span>
                              </Col>
                              <Col className="handle-col text-right">
                                <button
                                  onClick={() =>
                                    clickDeletePipelinePhase(data.id)
                                  }
                                  style={{
                                    border: "none",
                                    background: "none",
                                    padding: "0px",
                                    color: "grey",
                                    marginRight: "10px",
                                  }}
                                >
                                  <i className="far fa-trash-alt"></i>
                                </button>
                                <button
                                  style={{
                                    border: "none",
                                    background: "none",
                                    padding: "0px",
                                    color: "grey",
                                  }}
                                >
                                  <i className="fas fa-grip-horizontal"></i>
                                </button>
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </ReOrderableItem>
                    ))}
                  </ReOrderableList>
                </div>
              </div>
            ) : (
              <div className="no-units-container">
                <div className="inner-no-units-container">
                  <h2 className="no-units-logo">
                    <i class="fas fa-list"></i>
                  </h2>
                  <p className="no-units-description">
                    Du har ikke lagt til noen faser enda.
                  </p>
                </div>
              </div>
            )}
          </Col>
        </Row>
        <ToastContainer />
      </div>
    </Popup>
  );
};

export default Index;
